import type { FunctionComponent, RefObject } from 'react';
import classNames from 'classnames';
import { Plans } from '@fff-web/fff-ui-shared';
import {
  IReactivateMembershipContext,
  useReactivateMyMembershipContext,
} from '../../../utils/ContextProviders/ReactivateContext';
import { useReactivatePlanDataContext } from '../../../utils/ContextProviders/ReactivatePlanDataContext';
import useGetPricesByPlan from '../../../utils/hooks/useGetPricesByPlan';
import { IPlanCode } from '../../../utils/Interfaces';
import { GROWTH_MY_REACTIVATE_MEMBERSHIP } from '../../../utils/Optimizely/featureFlagVariations';
import * as styles from './MembershipPlans.module.scss';

export interface IMembershipPlansProps {
  planSectionRef: RefObject<HTMLDivElement>;
}

const MembershipPlans: FunctionComponent<IMembershipPlansProps> = ({ planSectionRef }) => {
  const { growthMyMembership } = useReactivateMyMembershipContext();
  const { variables, variationKey } = growthMyMembership as IReactivateMembershipContext;
  const { selectedPlanCode, onPlanChange } = useReactivatePlanDataContext();

  const { isLoadingPrices, seasonalPrice, annualPrice } = useGetPricesByPlan(growthMyMembership);

  const seasonalPerkList = variables.seasonalPerks?.list;
  const annualPerkList = variables.annualPerks?.list;

  const annualPlanSubtitle = variables?.annualCardSubtitle;

  const annualData = {
    title: 'Annual',
    subtitle: annualPlanSubtitle,
    price: annualPrice?.price,
    isLoadingPrice: isLoadingPrices,
    annualPrice: annualPrice?.annualPrice,
    isSelected: selectedPlanCode === IPlanCode.ANNUAL,
    onSelectPlan: () => onSelectPlanCode(IPlanCode.ANNUAL),
  };

  const seasonalData = {
    title: 'Seasonal',
    subtitle: 'Pay per box',
    price: seasonalPrice?.price,
    isLoadingPrice: isLoadingPrices,
    disclaimer: 'Billed quarterly.',
    isSelected: selectedPlanCode === IPlanCode.SEASONAL,
    onSelectPlan: () => onSelectPlanCode(IPlanCode.SEASONAL),
  };

  const onSelectPlanCode = (planCode: IPlanCode) => {
    onPlanChange(planCode);
  };

  const plansComponentStyles = classNames({
    [styles.customPlanStyles]: variationKey === GROWTH_MY_REACTIVATE_MEMBERSHIP.VARIATION_E,
  });

  return (
    <section ref={planSectionRef} className={styles.planSectionWrapper}>
      <div className={styles.planSectionTitle}>
        <h2>{variables.planSectionTitle}</h2>
        <h5>{variables.planSectionSubtitle}</h5>
      </div>
      <Plans
        componentStyles={plansComponentStyles}
        annualData={{
          ...annualData,
          perksList: annualPerkList,
        }}
        seasonalData={{
          ...seasonalData,
          perksList: seasonalPerkList,
        }}
      />
    </section>
  );
};

export default MembershipPlans;
